<template>
    <div class="modern-form-container">
        <div class="form-header">
            <div class="header-content">
                <div class="title-section">
                    <div class="icon-wrapper">
                        <i class="fas fa-file-medical"></i>
                    </div>
                    <div>
                        <h4 class="form-title">
                            {{ localNoteForm.id ? "Editar Reporte Médico" : "Nuevo Reporte Médico" }}
                            <span v-if="localNoteForm.id" class="id-badge">#{{ localNoteForm.id }}</span>
                        </h4>
                        <p class="form-subtitle">Registro de documentos e informes médicos</p>
                    </div>
                </div>
                <div class="action-buttons">
                    <ArgonButton
variant="gradient" color="success" :disabled="isSaving" size="md"
                        class="header-btn-primary" @click="handleSubmit">
                        <i :class="localNoteForm.id ? 'fas fa-save' : 'fas fa-plus-circle'" class="me-2"></i>
                        {{ isSaving ? 'Guardando...' : (localNoteForm.id ? 'Guardar Reporte' : 'Crear Reporte') }}
                    </ArgonButton>
                    <div v-if="isEditing" class="secondary-actions">
                        <ArgonButton
variant="gradient" color="primary" size="sm" class="header-btn-secondary pdf-btn"
                            :disabled="generatingPdf" data-bs-toggle="tooltip" title="Generar PDF del reporte"
                            @click="generateDocumentPdf">
                            <i v-if="!generatingPdf" class="fas fa-file-pdf"></i>
                            <i v-else class="fas fa-spinner fa-spin"></i>
                        </ArgonButton>
                        <ArgonButton
variant="gradient" color="success" size="sm" class="header-btn-secondary whatsapp-btn"
                            :disabled="sendingWhatsApp" data-bs-toggle="tooltip" title="Enviar PDF por WhatsApp"
                            @click="sendDocumentToWhatsApp">
                            <i v-if="!sendingWhatsApp" class="fab fa-whatsapp"></i>
                            <i v-else class="fas fa-spinner fa-spin"></i>
                        </ArgonButton>
                        <ArgonButton
variant="gradient" color="info" size="sm" class="header-btn-secondary"
                            data-bs-toggle="tooltip" title="Duplicar reporte" @click="handleDuplicateNote">
                            <i class="fas fa-copy"></i>
                        </ArgonButton>
                        <ArgonButton
variant="gradient" color="danger" size="sm" class="header-btn-secondary"
                            data-bs-toggle="tooltip" title="Eliminar reporte" @click="deleteNote('report')">
                            <i class="fas fa-trash"></i>
                        </ArgonButton>
                    </div>
                </div>
            </div>
        </div>

        <!-- Barra de estado profesional -->
        <div class="status-bar">
            <div class="status-left">
                <ProgressIndicator :form-data="localNoteForm" :compact="true" />
            </div>

            <div class="status-center">
                <!-- Notificación de borrador inline -->
                <transition name="slide-fade">
                    <div v-if="hasDraft" class="draft-badge">
                        <i class="fas fa-file-alt"></i>
                        <span>Borrador disponible</span>
                        <button class="draft-btn-restore" title="Restaurar borrador" @click="handleRestoreDraft">
                            <i class="fas fa-undo"></i>
                        </button>
                        <button class="draft-btn-discard" title="Descartar borrador" @click="handleDiscardDraft">
                            <i class="fas fa-times"></i>
                        </button>
                    </div>
                </transition>
            </div>

            <div class="status-right">
                <AutosaveIndicator
:last-save-time="lastSaveTime" :has-unsaved-changes="hasUnsavedChanges"
                    :has-draft="hasDraft" :formatted-save-time="getFormattedSaveTime()" :compact="true" :inline="true"
                    @restore-draft="handleRestoreDraft" @discard-draft="handleDiscardDraft" />

                <!-- Plantillas -->
                <HTMLSelectorModal
v-model="localNoteForm.format_html_id" :label="'Seleccionar Plantilla'"
                    :can-edit-and-add="true" :editor-content="localNoteForm.report || ''"
                    @template-selected="addTemplateContentToEditor" @update:editor-content="updateEditorContent">
                    <template #trigger="{ open }">
                        <ArgonButton
                            variant="gradient"
                            color="info"
                            size="sm"
                            class="mb-0 me-1"
                            @click="open"
                        >
                            <i class="fas fa-file-code me-1"></i>
                            Plantillas
                        </ArgonButton>
                    </template>
                </HTMLSelectorModal>

                <!-- Icono de ayuda de teclado -->
                <button
type="button" class="keyboard-help-btn" title="Atajos de teclado (Ctrl+?)"
                    @click="toggleKeyboardHelp">
                    <i class="fas fa-keyboard"></i>
                </button>
            </div>
        </div>

        <form class="modern-form" @submit.prevent="handleSubmit">
            <!-- Sección de reporte -->
            <div class="form-section">
                <div class="section-title enhanced">
                    <div class="title-left">
                        <i class="fas fa-file-alt"></i>
                        <span>Contenido del Documento</span>
                        <span class="required-badge">Requerido</span>
                    </div>
                </div>

                <div class="form-group enhanced">
                    <label for="report" class="form-label modern">
                        <span class="label-content">
                            <i class="fas fa-edit"></i>
                            <span>Reporte médico</span>
                        </span>
                    </label>
                    <div class="editor-wrapper" :class="{ 'has-error': errors.report }">
                        <quill-editor
ref="reportEditorRef" v-model:value="localNoteForm.report"
                            :options="$editorOptions" class="editor modern" @focus="activeEditor = 'report'"
                            @blur="activeEditor = null" />
                        <div class="editor-footer">
                            <div class="editor-stats">
                                <span class="stat-item">
                                    <i class="fas fa-font"></i>
                                    {{ getTextLength(localNoteForm.report) }} caracteres
                                </span>
                                <span class="stat-item">
                                    <i class="fas fa-align-left"></i>
                                    {{ getWordCount(localNoteForm.report) }} palabras
                                </span>
                            </div>
                        </div>
                    </div>
                    <transition name="error-slide-down">
                        <div v-if="errors.report" class="error-message-modern">
                            <i class="fas fa-exclamation-triangle"></i>
                            <span>{{ getErrorMessage(errors.report) }}</span>
                        </div>
                    </transition>
                    <div v-if="!errors.report && !localNoteForm.report" class="help-text-modern">
                        <i class="fas fa-lightbulb"></i>
                        <span>Describe detalladamente el contenido del documento médico</span>
                    </div>
                </div>
            </div>

            <!-- Sección de imágenes (solo si existe ID) -->
            <div v-if="localNoteForm.id" class="form-section">
                <div class="section-title">
                    <i class="fas fa-images"></i>
                    <span>Imágenes del Documento</span>
                </div>
                <UploadReportImages
:key="localNoteForm.id || 'new-document'" :patient-record="patientRecord"
                    :local-note-form="localNoteForm" @update:images-url="handleUpdateImagesUrl" />
            </div>

            <!-- Sección de diagnósticos -->
            <div class="form-section">
                <div class="section-title">
                    <i class="fas fa-stethoscope"></i>
                    <span>Diagnósticos CIE-10</span>
                </div>
                <div class="diagnoses-grid">
                    <div class="grid-item">
                        <label class="form-label modern">
                            <span class="label-content">
                                <i class="fas fa-star"></i>
                                <span>Diagnóstico Principal</span>
                            </span>
                        </label>
                        <MenuSelectedFull
v-model="localNoteForm.diagnosis_cie10_1" :label="''" apiurl="cie10"
                            :can-edit-and-add="false" />
                    </div>
                    <div class="grid-item">
                        <label class="form-label modern">
                            <span class="label-content">
                                <i class="fas fa-plus"></i>
                                <span>Diagnóstico Secundario 1</span>
                            </span>
                        </label>
                        <MenuSelectedFull
v-model="localNoteForm.diagnosis_cie10_2" :label="''" apiurl="cie10"
                            :can-edit-and-add="false" />
                    </div>
                    <div class="grid-item">
                        <label class="form-label modern">
                            <span class="label-content">
                                <i class="fas fa-plus"></i>
                                <span>Diagnóstico Secundario 2</span>
                            </span>
                        </label>
                        <MenuSelectedFull
v-model="localNoteForm.diagnosis_cie10_3" :label="''" apiurl="cie10"
                            :can-edit-and-add="false" />
                    </div>
                </div>
            </div>

            <!-- Sección de fecha -->
            <div class="form-section">
                <div class="section-title">
                    <i class="fas fa-calendar-alt"></i>
                    <span>Información de Creación</span>
                </div>
                <div class="form-group">
                    <label class="form-label modern">
                        <span class="label-content">
                            <i class="fas fa-clock"></i>
                            <span>Fecha de creación</span>
                        </span>
                    </label>
                    <DatePicker v-model="localNoteForm.created" :errors="errors" error-field-name="created" />
                </div>
            </div>

            <!-- Navegación de documentos (solo en modo editar) -->
            <div v-if="isEditing && filteredNotes.length > 0" class="notes-navigation">
                <NotesFiltersButtons
:filtered-notes="filteredNotes" :current-id="currentId"
                    :select-note="selectNoteForm" :format-date="formatDate" record-type="documento" />
            </div>
        </form>

        <!-- Ayuda de atajos de teclado -->
        <KeyboardShortcutsHelp ref="keyboardHelpRef" :is-mac="isMac" />
    </div>
</template>

<script setup>
import { ref, watch, onMounted, nextTick, getCurrentInstance, computed } from 'vue';
import { useNoteFormGlobal } from '@/composables/useNoteFormGlobal';
import { useKeyboardShortcuts } from '@/composables/useKeyboardShortcuts';
import { useAutosave } from '@/composables/useAutosave';
import api from '@/services/api';
// ✅ Import absoluto corregido
import NotesFiltersButtons from '@/components/NotesFiltersButtons.vue';
import DatePicker from "@/components/DatePicker.vue";
import HTMLSelectorModal from '@/components/HTMLSelectorModal.vue';
import UploadReportImages from '@/components/UploadReportImages.vue';
import MenuSelectedFull from '@/components/MenuSelectedFull.vue';
import ArgonButton from '@/components/ArgonButton.vue';
import KeyboardShortcutsHelp from '@/components/KeyboardShortcutsHelp.vue';
import AutosaveIndicator from '@/components/AutosaveIndicator.vue';
import ProgressIndicator from '@/components/ProgressIndicator.vue';
import { quillEditor } from 'vue3-quill';

const { proxy } = getCurrentInstance();

// Estado para loading y UI
const isSaving = ref(false);
const activeEditor = ref(null);
const generatingPdf = ref(false);
const sendingWhatsApp = ref(false);

// Referencias
const reportEditorRef = ref(null);

const props = defineProps({
    notes: {
        type: Array,
        default: () => [],
    },
    noteForm: {
        type: Object,
        default: () => ({
            created: new Date().toISOString().split('T')[0]
        })
    },
    currentId: {
        type: Number,
        default: 0,
        required: true,
    },
    selectNote: {
        type: Function,
        required: true,
    },
    errors: {
        type: Object,
        default: () => ({}),
    },
    patientRecord: {
        type: Object,
        required: true
    },
    activeTabPrefix: {
        type: String,
        required: true
    }
});

const emit = defineEmits({
    'save-note': null,
    'reset-form': null,
    'update:currentId': null,
    'delete-note': null,
    'update:activeTab': null,
    'clear-field-error': null,
    'clear-all-errors': null
});

const {
    localNoteForm,
    filteredNotes,
    handleSubmit: originalHandleSubmit,
    selectNoteForm,
    deleteNote,
    handleDuplicateNote,
    formatDate,
    isEditing
} = useNoteFormGlobal(props, emit, props.activeTabPrefix);

// Wrapper para handleSubmit
const handleSubmit = async () => {
    await originalHandleSubmit();
};

// 📄 Función para generar PDF de Reporte Médico
const generateDocumentPdf = async () => {
    if (generatingPdf.value) return;

    if (!localNoteForm.value.id) {
        proxy.$notify(
            'Debes guardar el reporte antes de generar el PDF',
            '',
            'warning',
            'ni ni-bell-55 ni-lg'
        );
        return;
    }

    try {
        generatingPdf.value = true;

        console.log('📄 Generando PDF de Reporte Médico:', localNoteForm.value.id);

        const response = await api.post('/generate-document-pdf', {
            document_id: localNoteForm.value.id
        });

        if (response.data && response.data.data && response.data.data.pdf_url) {
            const pdfUrl = response.data.data.pdf_url;

            // Agregar timestamp para evitar caché del navegador
            const urlWithTimestamp = pdfUrl.includes('?')
                ? `${pdfUrl}&t=${Date.now()}`
                : `${pdfUrl}?t=${Date.now()}`;

            // Abrir PDF en nueva pestaña
            window.open(urlWithTimestamp, '_blank');

            // Notificación de éxito
            proxy.$notify(
                response.data.message || 'Reporte Médico generado exitosamente',
                '',
                'success',
                'ni ni-like-2 ni-lg'
            );
        } else {
            throw new Error('Respuesta inválida del servidor');
        }

    } catch (error) {
        console.error('❌ Error al generar PDF de Reporte Médico:', error);

        const errorMessage = error.response?.data?.message ||
            error.message ||
            'No se pudo generar el PDF. Intenta nuevamente.';

        proxy.$notify(
            errorMessage,
            '',
            'danger',
            'ni ni-fat-remove ni-lg'
        );
    } finally {
        generatingPdf.value = false;
    }
};

// 📱 Función para enviar documento PDF por WhatsApp
const sendDocumentToWhatsApp = async () => {
    if (sendingWhatsApp.value) return;

    if (!localNoteForm.value.id) {
        proxy.$notify(
            'Debes guardar el reporte antes de enviarlo por WhatsApp',
            '',
            'warning',
            'ni ni-bell-55 ni-lg'
        );
        return;
    }

    try {
        sendingWhatsApp.value = true;

        console.log('📱 Enviando documento PDF por WhatsApp:', localNoteForm.value.id);

        const response = await api.post('/whatsapp/send-document-pdf', {
            document_id: localNoteForm.value.id
        });

        if (response.data) {
            // Notificación de éxito
            proxy.$notify(
                response.data.message || 'Documento enviado por WhatsApp exitosamente',
                '',
                'success',
                'ni ni-like-2 ni-lg'
            );
        } else {
            throw new Error('Respuesta inválida del servidor');
        }

    } catch (error) {
        console.error('❌ Error al enviar documento por WhatsApp:', error);

        const errorMessage = error.response?.data?.message ||
            error.message ||
            'No se pudo enviar el documento por WhatsApp. Intenta nuevamente.';

        proxy.$notify(
            errorMessage,
            '',
            'danger',
            'ni ni-fat-remove ni-lg'
        );
    } finally {
        sendingWhatsApp.value = false;
    }
};

// Asegúrate de que localNoteForm.report esté definido
localNoteForm.report = localNoteForm.report || '';

// Funciones helper
const getTextLength = (html) => {
    if (!html) return 0;
    const temp = document.createElement('div');
    temp.innerHTML = html;
    return temp.textContent.trim().length;
};

const getWordCount = (html) => {
    if (!html) return 0;
    const temp = document.createElement('div');
    temp.innerHTML = html;
    const text = temp.textContent.trim();
    return text ? text.split(/\s+/).length : 0;
};

const getErrorMessage = (error) => {
    if (!error) return '';
    if (Array.isArray(error)) return error[0];
    return error;
};

// ⌨️ Configurar atajos de teclado
const { isMac } = useKeyboardShortcuts({
    onSave: handleSubmit,
    onNew: () => {
        emit('reset-form');
        emit('update:activeTab', 'create');
    },
    onList: () => {
        emit('update:activeTab', 'list');
    },
    onCancel: () => {
        if (props.currentId && props.currentId > 0) {
            emit('update:activeTab', 'view');
        }
    }
});

// 💾 Configurar auto-guardado
const autosaveKey = computed(() => `document-draft-${props.patientRecord?.id || 'temp'}`);
const fieldsToAutosave = [
    'report',
    'diagnosis_cie10_1',
    'diagnosis_cie10_2',
    'diagnosis_cie10_3',
    'created'
];

const {
    lastSaveTime,
    hasUnsavedChanges,
    hasDraft,
    loadDraft,
    discardDraft,
    getFormattedSaveTime
} = useAutosave(
    localNoteForm,
    autosaveKey,
    30000, // 30 segundos
    fieldsToAutosave
);

// Restaurar borrador
const handleRestoreDraft = () => {
    const draft = loadDraft();
    if (draft) {
        Object.assign(localNoteForm.value, draft);
    }
};

// Descartar borrador
const handleDiscardDraft = () => {
    discardDraft();
};

// Watch para limpiar errores
watch(
    () => localNoteForm.value.report,
    (newValue) => {
        if (newValue && props.errors.report) {
            emit('clear-field-error', 'report');
        }
    }
);

watch(
    () => localNoteForm.value.created,
    (newValue) => {
        if (newValue && props.errors.created) {
            emit('clear-field-error', 'created');
        }
    }
);

// Método para mostrar/ocultar ayuda de teclado
const keyboardHelpRef = ref(null);

const toggleKeyboardHelp = () => {
    if (keyboardHelpRef.value) {
        keyboardHelpRef.value.toggleHelp();
    }
};

// Funciones del editor
function addTemplateContentToEditor(template) {
    const { name, content } = template;
    const editor = reportEditorRef.value?.__quill || reportEditorRef.value?.$el?.__quill;

    if (editor) {
        const newContent = `<p style="text-align: center;"><strong>${name}</strong><br>${content}</p>`;
        editor.root.innerHTML = newContent;
        localNoteForm.report = editor.root.innerHTML;
        proxy.$notify(`${name} agregado`, "", "success", "ni ni-like-2 ni-lg");
    } else {
        proxy?.$notify("El editor Quill no está inicializado.", "", "danger", "ni ni-fat-remove ni-lg");
    }
}

function updateEditorContent(content) {
    localNoteForm.report = content;
}

function handleUpdateImagesUrl(imagesUrl) {
    localNoteForm.images_url = imagesUrl;
}

onMounted(async () => {
    // No hacer scroll aquí - el MenuBar maneja el scroll cuando es necesario

    await nextTick();
    const editorReport = reportEditorRef.value?.__quill || reportEditorRef.value?.$el?.__quill;

    if (editorReport) {
        localNoteForm.report = editorReport.root.innerHTML;
    } else {
        proxy?.$notify("El editor Quill del reporte no está inicializado.", "", "danger", "ni ni-fat-remove ni-lg");
    }
});

// Watch para cuando cambie la nota (sin scroll - MenuBar lo maneja)
watch(() => props.note, () => {
    if (props.note) {
        // No hacer scroll aquí
    }
});
</script>

<style scoped>
/* Standardized Header */
.modern-form-container {
    --accent: #3b82f6;
    --accent-strong: #2563eb;
    --accent-soft: #eff6ff;
    --border: #f1f5f9;
    --surface: #ffffff;
    --muted-surface: #f8fafc;
    --text: #334155;
    --muted-text: #64748b;
    --shadow-sm: 0 1px 2px 0 rgb(0 0 0 / 0.05);
    --shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1);
    margin-bottom: 24px;
    background: var(--surface);
    border-radius: 16px;
    border: 1px solid var(--border);
    box-shadow: var(--shadow);
    overflow: hidden;
}

.form-header {
    background: white;
    border-bottom: 1px solid var(--border);
    padding: 1.5rem;
}

.header-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 1rem;
}

.title-section {
    display: flex;
    align-items: center;
    gap: 1rem;
}

.icon-wrapper {
    background: var(--accent-soft);
    border-radius: 12px;
    width: 48px;
    height: 48px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.25rem;
    color: var(--accent);
}

.form-title {
    margin: 0;
    font-size: 1.25rem;
    font-weight: 600;
    color: var(--text);
    letter-spacing: -0.025em;
    display: flex;
    align-items: center;
    gap: 0.75rem;
}

.id-badge {
    display: inline-flex;
    align-items: center;
    padding: 0.25rem 0.75rem;
    background: var(--muted-surface);
    border: 1px solid var(--border);
    border-radius: 9999px;
    font-size: 0.75rem;
    font-weight: 600;
    color: var(--muted-text);
}

.form-subtitle {
    margin: 0.25rem 0 0;
    color: var(--muted-text);
    font-size: 0.875rem;
}

.action-buttons {
    display: flex;
    align-items: center;
    gap: 0.75rem;
}

.header-btn-primary {
    box-shadow: var(--shadow-sm) !important;
    font-weight: 500 !important;
    font-size: 0.875rem !important;
    padding: 0.625rem 1.25rem !important;
    border-radius: 8px !important;
    text-transform: none !important;
    background: var(--accent) !important;
    border: 1px solid var(--accent-strong) !important;
    color: white !important;
    transition: all 0.2s ease !important;
}

.header-btn-primary:hover {
    background: var(--accent-strong) !important;
    transform: translateY(-1px) !important;
}

.header-btn-secondary {
    box-shadow: none !important;
    padding: 0.5rem !important;
    border-radius: 8px !important;
    background: white !important;
    border: 1px solid var(--border) !important;
    color: var(--muted-text) !important;
    transition: all 0.2s ease !important;
}

.header-btn-secondary:hover {
    border-color: var(--accent) !important;
    color: var(--accent) !important;
    background: var(--accent-soft) !important;
}

.secondary-actions {
    display: flex;
    gap: 0.5rem;
    align-items: center;
}

.modern-form {
    background: var(--surface);
    padding: 1.5rem;
}

.status-bar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.75rem 1.5rem;
    background: var(--muted-surface);
    border-bottom: 1px solid var(--border);
    gap: 1rem;
}

.draft-badge {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.375rem 0.75rem;
    background: white;
    border: 1px solid var(--border);
    border-radius: 9999px;
    color: var(--muted-text);
    font-size: 0.75rem;
    font-weight: 500;
}

.draft-badge i {
    color: var(--accent);
}

.draft-btn-restore,
.draft-btn-discard {
    padding: 0.25rem;
    border-radius: 4px;
    cursor: pointer;
    font-size: 0.75rem;
    transition: all 0.2s ease;
    border: none;
    background: transparent;
    color: var(--muted-text);
}

.draft-btn-restore:hover {
    color: var(--accent);
    background: var(--accent-soft);
}

.draft-btn-discard:hover {
    color: #ef4444;
    background: #fee2e2;
}

.keyboard-help-btn {
    padding: 0.5rem;
    background: white;
    border: 1px solid var(--border);
    border-radius: 8px;
    color: var(--muted-text);
    cursor: pointer;
    font-size: 0.875rem;
    transition: all 0.2s ease;
}

.keyboard-help-btn:hover {
    border-color: var(--accent);
    color: var(--accent);
}

.form-section {
    background: white;
    border-radius: 12px;
    padding: 1.5rem;
    margin-bottom: 1.5rem;
    border: 1px solid var(--border);
    box-shadow: var(--shadow-sm);
}

.section-title {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-bottom: 1.25rem;
    font-weight: 600;
    color: var(--text);
    font-size: 1rem;
}

.section-title i {
    color: var(--accent);
    font-size: 1.1rem;
}

.section-title.enhanced {
    background: transparent;
    border: none;
    border-bottom: 1px solid var(--border);
    padding: 0 0 1rem 0;
    margin: 0 0 1.5rem 0;
    border-radius: 0;
}

.form-label {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 0.5rem;
    font-weight: 500;
    color: var(--text);
    font-size: 0.875rem;
}

.form-label.modern {
    justify-content: space-between;
    margin-bottom: 0.75rem;
}

.label-content {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
}

.label-content i {
    color: var(--muted-text);
    font-size: 0.875rem;
}

.required-badge {
    background: var(--accent-soft);
    color: var(--accent);
    font-size: 0.7rem;
    padding: 0.125rem 0.5rem;
    border-radius: 9999px;
    font-weight: 600;
}

.editor-wrapper {
    border-radius: 8px;
    overflow: hidden;
    border: 1px solid var(--border);
    background: white;
    transition: all 0.2s ease;
}

.editor-wrapper:focus-within {
    border-color: var(--accent);
    box-shadow: 0 0 0 3px var(--accent-soft);
}

.editor-wrapper.has-error {
    border-color: #ef4444;
}

.error-message-modern {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-top: 0.5rem;
    color: #ef4444;
    font-size: 0.875rem;
}

.help-text-modern {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-top: 0.5rem;
    color: var(--muted-text);
    font-size: 0.875rem;
}

:deep(.ql-toolbar) {
    background: var(--muted-surface);
    border: none;
    border-bottom: 1px solid var(--border);
    padding: 0.75rem;
}

:deep(.ql-container) {
    border: none !important;
    font-size: 0.95rem;
    font-family: inherit;
}

:deep(.ql-editor) {
    min-height: 150px;
    padding: 1rem;
}

.editor-footer {
    background: var(--muted-surface);
    border-top: 1px solid var(--border);
    padding: 0.5rem 1rem;
}

.editor-stats {
    display: flex;
    gap: 1rem;
}

.stat-item {
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    font-size: 0.75rem;
    color: var(--muted-text);
}

.diagnoses-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 1.25rem;
}

.grid-item {
    background: var(--muted-surface);
    padding: 1.25rem;
    border-radius: 8px;
    border: 1px solid var(--border);
}

.notes-navigation {
    background: var(--muted-surface);
    border-radius: 12px;
    padding: 1rem;
    margin-top: 1.5rem;
    border: 1px solid var(--border);
}

.status-right {
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

@media (max-width: 768px) {
    .status-bar {
        flex-direction: column;
        gap: 1rem;
        padding: 1rem;
    }

    .status-left,
    .status-center,
    .status-right {
        width: 100%;
        justify-content: center;
    }

    .header-content {
        flex-direction: column;
        text-align: center;
    }

    .modern-form {
        padding: 1rem;
    }
}

/* Estilos para el botón PDF */
.pdf-btn {
    min-width: 42px;
    height: 42px;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s ease;
}

.pdf-btn:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(233, 30, 99, 0.3);
}

.pdf-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.pdf-btn .fa-spinner {
    animation: spin 1s linear infinite;
}

@keyframes spin {
    from {
        transform: rotate(0deg);
    }
    to {
        transform: rotate(360deg);
    }
}
</style>
